import type { ReactNode } from 'react'

import type { StockBalanceSummary, StockColumnId } from '@aeris/contracts'
import {
  ZERO_QUANTITY,
  formatQuantity as formatUnits,
  parseQuantity,
} from '@aeris/inventory'
import {
  COMMERCIAL_ROUNDING,
  MONEY_SCALE,
  ZERO_MONEY,
  formatMoney,
  formatPercent,
  fromString,
  isNegative,
  multiply,
  parseHumanAmount,
  round,
  subtract,
} from '@aeris/money'
import { marginFromPrice } from '@aeris/pricing'
import { Badge } from '@aeris/ui'

import { formatDate } from './date-format.ts'
import type { ColumnRule } from './table-columns.ts'

/**
 * As colunas da lista de Estoque — F167.
 *
 * No molde de `product-columns.tsx`, e pela mesma razão: uma coluna nova é uma
 * entrada nesta lista, e o id dela tem de existir em `STOCK_COLUMN_IDS`, que é
 * o que o servidor aceita gravar.
 *
 * ## Por que o dinheiro apareceu aqui
 *
 * Até a F163 esta tela respondia "quanto tem". Com o catálogo inteiro dentro do
 * sistema (5.531 peças), a pergunta do dono virou "quanto isso vale, e quanto
 * eu ganho" — e a resposta estava só no total do filtro. Custo, preço, margem
 * e os dois valores por linha são o mesmo número, linha a linha.
 *
 * Custo e margem exigem `catalog.write`, como na lista de Produtos: quem não
 * edita o produto não vê o que a loja pagou.
 */

export type StockCellContext = {
  /** Abre o histórico da peça — o que clicar na linha faz. */
  readonly onOpen: (balance: StockBalanceSummary) => void
  /**
   * O que se procurou, para a linha poder dizer POR ONDE casou — F190.
   *
   * O dono digitou `8810` e recebeu sete peças sem nenhuma relação aparente.
   * Todas casaram pelo **código de barras** — a barra interna da loja carrega o
   * mesmo pedaço em quase toda peça —, e a barra é justamente a única das três
   * colunas procuradas que a tela **não mostra por padrão**. Do lado de cá, o
   * produto veio do nada.
   */
  readonly search?: string | undefined
}

/**
 * A linha casou só pela barra? — F190.
 *
 * Só quando o texto não está no código nem no nome: se estivesse, a pessoa já
 * está vendo por que a linha veio, e repetir a barra seria ruído em toda busca.
 */
export const matchedOnlyByBarcode = (
  balance: StockBalanceSummary,
  search: string | undefined
): boolean => {
  const term = (search ?? '').trim().toLowerCase()
  if (term === '' || balance.gtin === null) return false
  if (!balance.gtin.toLowerCase().includes(term)) return false
  return (
    !balance.sku.toLowerCase().includes(term) &&
    !balance.productName.toLowerCase().includes(term)
  )
}

/**
 * As colunas por que o Estoque sabe se ordenar — F185.
 *
 * Todas já saem na consulta da página, então nenhuma custou junção nova.
 */
export type StockSortKey =
  | 'name'
  | 'sku'
  | 'brand'
  | 'category'
  | 'onHand'
  | 'reserved'
  | 'available'
  | 'averageCost'
  | 'salePrice'
  | 'margin'
  | 'costValue'
  | 'saleValue'
  | 'updatedAt'

export type StockColumn = ColumnRule<StockColumnId> & {
  readonly headerClassName?: string
  readonly sortKey?: StockSortKey
  readonly numeric?: boolean
  readonly className?: string
  readonly cell: (
    balance: StockBalanceSummary,
    context: StockCellContext
  ) => ReactNode
}

/** As colunas de antes da F167: quem nunca escolheu vê a lista de sempre. */
export const DEFAULT_STOCK_COLUMNS: readonly StockColumnId[] = [
  'sku',
  'name',
  'unit',
  'onHand',
  'reserved',
  'available',
  'updatedAt',
]

/**
 * Do texto decimal exato, nunca de um `Number()` (ADR-0016).
 *
 * Nulo vira traço e não zero: "ninguém informou o custo" e "custou zero" são
 * coisas diferentes, e é a diferença que faz o lucro da tela ser verdade.
 */
const asMoney = (value: string | null | undefined): ReactNode =>
  value == null ? (
    <span className="text-(--text-muted)">—</span>
  ) : (
    formatMoney(parseHumanAmount(value, MONEY_SCALE) ?? ZERO_MONEY)
  )

const asQuantity = (value: string): string =>
  formatUnits(parseQuantity(value) ?? ZERO_QUANTITY)

/** Saldo × valor unitário: o dinheiro que aquela linha representa. */
const valueOf = (
  balance: StockBalanceSummary,
  unit: string | null | undefined
): ReactNode => {
  if (unit == null) return <span className="text-(--text-muted)">—</span>
  const quantity = parseQuantity(balance.onHand)
  if (quantity === null) return <span className="text-(--text-muted)">—</span>
  // Arredondado ao centavo só na hora de mostrar: `multiply` soma as escalas,
  // e 7 peças × 12,345678 não é um valor que alguém leia.
  return formatMoney(
    round(
      multiply(fromString(unit), quantity),
      MONEY_SCALE,
      COMMERCIAL_ROUNDING
    )
  )
}

export const STOCK_COLUMNS: readonly StockColumn[] = [
  {
    id: 'sku',
    sortKey: 'sku',
    label: 'Código',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap',
    /*
     * F190: quando a linha só veio por causa da barra, a barra aparece — mesmo
     * com a coluna "Código de barras" desligada, que é o estado padrão. Uma
     * linha que aparece sem dizer por quê é a diferença entre "o sistema achou
     * algo" e "o sistema puxou um monte semelhante".
     */
    cell: (balance, context) =>
      matchedOnlyByBarcode(balance, context.search) ? (
        <span className="flex flex-col">
          {balance.sku}
          <span className="text-(length:--text-xs) text-(--text-muted)">
            barras {balance.gtin}
          </span>
        </span>
      ) : (
        balance.sku
      ),
  },
  {
    id: 'gtin',
    label: 'Código de barras',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap',
    cell: (balance) =>
      balance.gtin ?? <span className="text-(--text-muted)">—</span>,
  },
  {
    id: 'name',
    label: 'Produto',
    requires: [],
    locked: true,
    sortKey: 'name',
    className: 'text-(--text-primary)',
    cell: (balance) => (
      <>
        {balance.productName}
        {balance.minimumStock != null &&
        isNegative(
          subtract(
            parseQuantity(balance.onHand) ?? ZERO_QUANTITY,
            parseQuantity(balance.minimumStock) ?? ZERO_QUANTITY
          )
        ) ? (
          <Badge tone="warning" className="ml-2">
            Abaixo do mínimo (mín. {asQuantity(balance.minimumStock)})
          </Badge>
        ) : null}
      </>
    ),
  },
  {
    id: 'brand',
    sortKey: 'brand',
    label: 'Marca',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (balance) =>
      balance.brand ?? <span className="text-(--text-muted)">—</span>,
  },
  {
    id: 'category',
    sortKey: 'category',
    label: 'Categoria',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (balance) =>
      balance.categoryName ?? <span className="text-(--text-muted)">—</span>,
  },
  {
    id: 'unit',
    label: 'Un.',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (balance) => balance.unit,
  },
  {
    id: 'onHand',
    label: 'Em mãos',
    requires: [],
    numeric: true,
    sortKey: 'onHand',
    cell: (balance) => asQuantity(balance.onHand),
  },
  {
    id: 'reserved',
    sortKey: 'reserved',
    label: 'Reservado',
    requires: [],
    numeric: true,
    className: 'text-(--text-muted)',
    cell: (balance) => asQuantity(balance.reserved),
  },
  {
    id: 'available',
    sortKey: 'available',
    label: 'Disponível',
    requires: [],
    numeric: true,
    className: 'font-(--weight-medium)',
    cell: (balance) => asQuantity(balance.available),
  },
  {
    id: 'cost',
    sortKey: 'averageCost',
    label: 'Custo médio',
    requires: ['catalog.write'],
    numeric: true,
    cell: (balance) => asMoney(balance.averageCost),
  },
  {
    id: 'price',
    sortKey: 'salePrice',
    label: 'Preço de venda',
    requires: ['pricing.read'],
    numeric: true,
    cell: (balance) => asMoney(balance.salePrice),
  },
  {
    id: 'margin',
    sortKey: 'margin',
    label: 'Margem',
    requires: ['catalog.write', 'pricing.read'],
    numeric: true,
    cell: (balance) => {
      if (balance.salePrice == null || balance.averageCost == null) {
        return <span className="text-(--text-muted)">—</span>
      }
      /*
       * `marginFromPrice(custo, preço)` — nesta ordem (F185).
       *
       * Estavam TROCADOS: a coluna calculava (custo − preço) / preço, então
       * uma peça comprada a R$ 10 e vendida a R$ 15 aparecia com −33,33% de
       * margem em vez de 50%. Toda peça com lucro lia como prejuízo.
       */
      const margin = marginFromPrice(
        fromString(balance.averageCost),
        fromString(balance.salePrice)
      )
      return margin === null ? (
        <span className="text-(--text-muted)">—</span>
      ) : (
        formatPercent(margin)
      )
    },
  },
  {
    id: 'costValue',
    sortKey: 'costValue',
    label: 'Valor em custo',
    requires: ['catalog.write'],
    numeric: true,
    cell: (balance) => valueOf(balance, balance.averageCost),
  },
  {
    id: 'saleValue',
    sortKey: 'saleValue',
    label: 'Valor em venda',
    requires: ['pricing.read'],
    numeric: true,
    cell: (balance) => valueOf(balance, balance.salePrice),
  },
  {
    id: 'updatedAt',
    sortKey: 'updatedAt',
    label: 'Último movimento',
    requires: [],
    className: 'whitespace-nowrap text-(--text-secondary)',
    cell: (balance) =>
      balance.updatedAt === null ? 'nunca' : formatDate(balance.updatedAt),
  },
]
